import {
  OrderType,
  QBankStructureLevelCategory,
  SearchUsersContentVisibilityDocument,
  useUpdateUserToQBankStructureMutation,
} from '@exam-management/graphql';
import { levelItemsToContentType } from '@exam-management/qbank-structure-cv-list';
import { QBankStructureCvListWithSelectedUsersForm } from '@exam-management/qbank-structure-cv-list-with-selected-users-form';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { useParams } from '@exam-management/router';
import {
  type OnSelectUserCallback,
  type UserRow,
  UserList,
  UserListParamsVariants,
  UserListVariants,
} from '@exam-management/user-management';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type BoxProps,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { type MouseEventHandler, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

const initialDataGridParams = {
  field: 'firstname',
  sort: OrderType.Asc,
  page: 0,
  pageSize: 10,
  role: '',
};

const MAX_USERS_TO_ADD = 8;

const AddUsersToQBankStructureFormSchema = z.object({
  userIds: z.array(z.string()).min(0).max(MAX_USERS_TO_ADD),
  contentIds: z
    .record(
      z.string(),
      z.object({
        isVisible: z.boolean(),
        type: z.nativeEnum(QBankStructureLevelCategory),
        parentLevelItemId: z.string().optional(),
      }),
    )
    .optional(),
});

interface SelectContentStructureFormProps extends BoxProps {
  usersDetails: UserRow[];
}

interface SelectMembersFormProps extends BoxProps {
  onSelectUser: OnSelectUserCallback;
}

type AddUsersToQBankStructureFormInput = z.infer<typeof AddUsersToQBankStructureFormSchema>;

enum AddUsersToQBankStructureFormStep {
  SELECT_USER = 'SELECT_USER',
  SELECT_CONTENT_STRUCTURE = 'SELECT_CONTENT_STRUCTURE',
}

function SelectMembersForm(props: Readonly<SelectMembersFormProps>) {
  const { onSelectUser, ...restProps } = props;
  const { watch } = useFormContext<AddUsersToQBankStructureFormInput>();

  const selectedUsers = watch('userIds', []);

  return (
    <Flex {...restProps}>
      <UserList
        variant={UserListVariants.QBANK_STRUCTURE_MEMBERS}
        shouldInvertFilter
        options={{
          withSearch: true,
          withSearchBoxFullWidth: true,
          withForm: true,
          withSelectCountText: true,
        }}
        paramsVariant={UserListParamsVariants.COMPONENT_STATE_PARAMS}
        DataGridProps={{
          autoHeight: false,
          rowHeight: 68,
          columnVisibilityModel: {
            appRoles: false,
            lastLoginAt: false,
          },
          sx: {
            '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'none',
            },
            '.MuiDataGrid-virtualScroller': {
              height: 340,
            },
          },
        }}
        SelectUsersCountProps={{
          count: selectedUsers.length,
          maxCount: MAX_USERS_TO_ADD,
        }}
        initialDataGridParams={initialDataGridParams}
        onSelectUser={onSelectUser}
      />
    </Flex>
  );
}

function SelectContentStructureForm(props: Readonly<SelectContentStructureFormProps>) {
  const { usersDetails, ...restProps } = props;
  return <QBankStructureCvListWithSelectedUsersForm usersDetails={usersDetails} {...restProps} />;
}

interface AddUsersToQBankStructureProps {
  open: boolean;
  onClose: () => void;
}

export default function AddUsersToQBankStructure(props: Readonly<AddUsersToQBankStructureProps>) {
  const { open: isOpen, onClose } = props;
  const { showSnackbar } = useSnackbar();
  const { structureId } = useParams('/qbank-structures/:structureId');

  const [formStep, setFormStep] = useState<AddUsersToQBankStructureFormStep>(
    AddUsersToQBankStructureFormStep.SELECT_USER,
  );

  const dialogContentRef = useRef<HTMLDivElement>(null);

  const [usersDetails, setUsersDetails] = useState<UserRow[]>([]);

  const { qBankStructureLevelItems, qBankStructureLevels } = useQBankStructure();

  const isPartLevelDisabled = qBankStructureLevels?.some(
    (a) => a.category === QBankStructureLevelCategory.Part && !a.enabled,
  );

  const isSubjectLevelDisabled = qBankStructureLevels?.some(
    (a) => a.category === QBankStructureLevelCategory.Subject && !a.enabled,
  );

  const isPartAndSubjectLevelDisabled = isPartLevelDisabled && isSubjectLevelDisabled;

  const courseId = useMemo(
    () =>
      qBankStructureLevelItems.find(
        (a) => a.qBankStructureLevel.category === QBankStructureLevelCategory.Course,
      )?.id ?? '',
    [qBankStructureLevelItems],
  );

  const formContext = useForm<AddUsersToQBankStructureFormInput>({
    defaultValues: { userIds: [], contentIds: {} },
    resolver: zodResolver(AddUsersToQBankStructureFormSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = formContext;

  const [updateUsersToQBankStructure] = useUpdateUserToQBankStructureMutation({
    onCompleted: (res) => {
      if (res?.qbsUpdateUsersQBankStructureLevelItemContentVisibility) {
        showSnackbar({ message: 'Member added successfully', variant: 'success' });
        handleClose();
      }
    },
    onError: () => {
      showSnackbar({ message: 'Member added unsuccessfully', variant: 'error' });
    },
    refetchQueries: [SearchUsersContentVisibilityDocument],
    awaitRefetchQueries: true,
  });

  const isSelectUserStep = formStep === AddUsersToQBankStructureFormStep.SELECT_USER;

  const scrollToTop = () => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const onBackStep: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    scrollToTop();
    setFormStep(AddUsersToQBankStructureFormStep.SELECT_USER);
  };

  const onNextStep: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    scrollToTop();
    setFormStep(AddUsersToQBankStructureFormStep.SELECT_CONTENT_STRUCTURE);
  };

  const handleClose = () => {
    onClose();
    formContext.reset({ userIds: [], contentIds: {} });
    setFormStep(AddUsersToQBankStructureFormStep.SELECT_USER);
  };

  const handleSelectUser: OnSelectUserCallback = (selectedUsers) => {
    setUsersDetails(selectedUsers);
  };

  const onSubmit = async (data: AddUsersToQBankStructureFormInput) => {
    const contentVisibilityIds = [];

    if (isPartAndSubjectLevelDisabled) {
      for (const [key, value] of Object.entries(data?.contentIds ?? {})) {
        contentVisibilityIds.push({
          contentId: key,
          parentContentId: value.parentLevelItemId,
          contentType: levelItemsToContentType[value.type],
        });
      }
    } else {
      for (const [key, value] of Object.entries(data?.contentIds ?? {})) {
        if (value.isVisible) {
          contentVisibilityIds.push({
            contentId: key,
            parentContentId: value.parentLevelItemId,
            contentType: levelItemsToContentType[value.type],
          });
        }
      }
    }

    await updateUsersToQBankStructure({
      variables: {
        input: {
          userIds: data.userIds,
          visibleContent: contentVisibilityIds,
          qbankStructureId: structureId,
          courseId,
        },
      },
    });
  };

  const isDisabledNextButton = Boolean(errors.userIds) || formContext.watch('userIds').length === 0;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='xl'>
      <DialogTitle onClose={handleClose}>Add Member</DialogTitle>
      <Divider />
      <FormProvider {...formContext}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent
            ref={dialogContentRef}
            sx={{
              maxHeight: isSelectUserStep ? 'none' : 'calc(100dvh - 64px)',
              p: (theme) => theme.spacing(6),
            }}
          >
            {!isSelectUserStep ? (
              <Text variant='h4' sx={{ pb: (theme) => theme.spacing(4) }}>
                Selected Member
              </Text>
            ) : null}
            {isSelectUserStep ? (
              <SelectMembersForm onSelectUser={handleSelectUser} sx={{ flexDirection: 'column' }} />
            ) : null}
            {formStep === AddUsersToQBankStructureFormStep.SELECT_CONTENT_STRUCTURE ? (
              <SelectContentStructureForm usersDetails={usersDetails} />
            ) : null}
          </DialogContent>
          <DialogActions
            sx={(theme) => ({
              position: isSelectUserStep ? 'static' : 'sticky',
              bottom: isSelectUserStep ? 'auto' : 0,
              backgroundColor: theme.palette.background.paper,
              borderTop: '1px solid',
              borderColor: theme.palette.grey[300],
            })}
          >
            <Box sx={{ mr: 'auto' }}>
              <Button
                onClick={handleClose}
                type='button'
                color='secondary'
                variant='outlined'
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </Box>
            {!isSelectUserStep ? (
              <Button
                onClick={onBackStep}
                type='button'
                color='secondary'
                variant='outlined'
                disabled={isSubmitting}
              >
                Back
              </Button>
            ) : null}
            {isSelectUserStep ? (
              <Button onClick={onNextStep} loading={isSubmitting} disabled={isDisabledNextButton}>
                Next
              </Button>
            ) : (
              <Button type='submit' loading={isSubmitting}>
                Confirm
              </Button>
            )}
          </DialogActions>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
